import React, { useMemo, useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { Users, Plus, Search, Filter, Calendar, Eye, RotateCcw, AlertTriangle, Edit, Archive, Trash2, X } from 'lucide-react';
import { useContacts } from '../hooks/useContacts';
import { ContactModal } from '../components/modals/ContactModal';
import { ArchiveModal } from '../components/shared/ArchiveModal';
import { DeleteModal } from '../components/shared/DeleteModal';
import { RestoreModal } from '../components/shared/RestoreModal';
import { useAuth } from '../hooks/useAuth';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import type { ContactWithActivity, CreateContactData, ContactsFilter } from '../../shared/types/entities';
import { toast } from 'sonner';

export const Contacts: React.FC = () => {
  const { isAdmin } = useAuth();
  const { hasRole: effectiveHasRole } = useEffectivePermissions();
  // Filters/state
  const [filter, setFilter] = useState<ContactsFilter>({
    search: '',
    contact_type: 'all',
  sortBy: 'updated_at',
    sortOrder: 'desc'
  });
  const [activeTab, setActiveTab] = useState<'all' | 'archived'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactWithActivity | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  const [archivedContacts, setArchivedContacts] = useState<ContactWithActivity[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const {
    contacts,
    loading,
    error,
    createContact,
    updateContact,
    archiveContact,
    restoreContact,
    deleteContact,
    getArchivedContacts,
    refetch
  } = useContacts(filter);

  // Load archived contacts when switching to archived tab
  useEffect(() => {
    if (activeTab === 'archived') {
      loadArchivedContacts();
    }
  }, [activeTab]);

  const loadArchivedContacts = async () => {
    setArchivedLoading(true);
    try {
      const archived = await getArchivedContacts();
      setArchivedContacts(archived);
    } catch (e) {
      console.error('Failed to load archived contacts', e);
    } finally {
      setArchivedLoading(false);
    }
  };

  // Archive/Delete handlers
  const handleArchiveClick = (contact: ContactWithActivity, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedContact(contact);
    setShowArchiveModal(true);
  };

  const handleDeleteClick = (contact: ContactWithActivity, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const handleRestoreClick = (contact: ContactWithActivity, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedContact(contact);
    setShowRestoreModal(true);
  };

  const handleArchiveContact = async () => {
    if (!selectedContact) return;
    try {
      await archiveContact(selectedContact.id);
      setShowArchiveModal(false);
      setSelectedContact(null);
      // Refresh the contacts list
      refetch();
      toast.success('Contact archived');
    } catch (e) {
      console.error('Failed to archive contact', e);
      toast.error('Failed to archive contact');
    }
  };

  const handleRestoreContact = async () => {
    if (!selectedContact) return;
    try {
      await restoreContact(selectedContact.id);
      setShowRestoreModal(false);
      setSelectedContact(null);
      // Remove from archived list
      setArchivedContacts(prev => prev.filter(c => c.id !== selectedContact.id));
      // Refresh main contacts list
      refetch();
      toast.success('Contact restored');
    } catch (e) {
      console.error('Failed to restore contact', e);
      toast.error('Failed to restore contact');
    }
  };

  const handleDeleteContactPermanent = async () => {
    if (!selectedContact) return;
    try {
      await deleteContact(selectedContact.id);
      setShowDeleteModal(false);
      setSelectedContact(null);
      // Remove from archived list if we're on archived tab
      if (activeTab === 'archived') {
        setArchivedContacts(prev => prev.filter(c => c.id !== selectedContact.id));
      }
      // Refresh the contacts list
      refetch();
      toast.success('Contact deleted');
    } catch (e) {
      console.error('Failed to delete contact', e);
      toast.error('Failed to delete contact');
    }
  };

  const handleDeleteContact = async (contact: ContactWithActivity, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await deleteContact(contact.id);
      toast.success('Contact deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete contact');
    }
  };

  const handleContactSelect = (contact: ContactWithActivity) => {
    setSelectedContact(contact);
    setModalMode('view');
    setShowModal(true);
  };

  const openCreateModal = () => {
    setSelectedContact(null);
    setModalMode('create');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => {
      setSelectedContact(null);
      setModalMode('view');
    }, 300);
  };

  const openEditModal = (contact: ContactWithActivity) => {
    setSelectedContact(contact);
    setModalMode('edit');
    setShowModal(true);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const getContactTypeStyles = (type: string) =>
    type === 'company'
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';

  const getContactTypeLabel = (type: string) => (type === 'company' ? 'Company' : 'Individual');

  const renderMobileContactCard = (
    contact: ContactWithActivity,
    { archived = false }: { archived?: boolean } = {}
  ) => {
    const artistCount = contact.artists_count || 0;

    return (
      <div
        key={contact.id}
        onClick={() => handleContactSelect(contact)}
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm transition hover:border-purple-200 dark:hover:border-purple-500/40 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center font-semibold text-white ${
              contact.contact_type === 'company'
                ? 'bg-purple-600 dark:bg-purple-600'
                : 'bg-blue-600 dark:bg-blue-600'
            }`}
          >
            {contact.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1">
            <div className="text-base font-semibold text-gray-900 dark:text-white">{contact.name}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {contact.company_name || 'No company'}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(contact.updated_at)}</span>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {artistCount} artists
          </span>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleContactSelect(contact)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Edit contact"
          >
            <Edit className="h-4 w-4" />
          </button>
          {archived ? (
            <>
              <button
                onClick={(e) => handleRestoreClick(contact, e)}
                className="p-2 rounded-full hover:bg-green-100 dark:hover:bg-green-900/20 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200"
                title="Restore contact"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              {isAdmin && (
                <button
                  onClick={(e) => handleDeleteClick(contact, e)}
                  className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                  title="Delete permanently"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={(e) => handleArchiveClick(contact, e)}
                className="p-2 rounded-full hover:bg-orange-100 dark:hover:bg-orange-900/20 text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-200"
                title="Archive contact"
              >
                <Archive className="h-4 w-4" />
              </button>
              {(effectiveHasRole('admin') || effectiveHasRole('super')) && (
                <button
                  onClick={(e) => handleDeleteClick(contact, e)}
                  className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                  title="Delete contact"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 py-6 sm:p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          icon={<Users className="h-8 w-8" />}
          title="Contacts"
          actions={(
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </button>
          )}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700/80 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            {[
              { id: 'all', label: 'All Contacts' },
              { id: 'archived', label: 'Archived' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'all' | 'archived')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600 dark:text-purple-300'
                    : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={filter.search}
                onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <select
                value={filter.contact_type || 'all'}
                onChange={(e) => setFilter(prev => ({ ...prev, contact_type: e.target.value as any }))}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All types</option>
                <option value="company">Companies</option>
                <option value="individual">Individuals</option>
              </select>
            </div>
            <button
              onClick={() => setFilter({ search: '', contact_type: 'all', sortBy: 'updated_at', sortOrder: 'desc' })}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Clear Filters
              </button>
          </div>
        </div>

        {/* Contacts List */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg md:overflow-hidden">
          {activeTab === 'all' && (
            <>
              {contacts.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No contacts found</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {filter.search ? 'Try adjusting your search terms.' : 'Get started by adding your first contact.'}
                  </p>
                  <button
                    onClick={openCreateModal}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contact
                  </button>
                </div>
              ) : (
                <>
                  <div className="md:hidden space-y-3 p-4">
                    {contacts.map((contact) => renderMobileContactCard(contact))}
                  </div>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Last Activity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Activity
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => handleContactSelect(contact)}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center font-semibold text-white ${
                                  contact.contact_type === 'company' 
                                    ? 'bg-purple-600 dark:bg-purple-600' 
                                    : 'bg-blue-600 dark:bg-blue-600'
                                }`}>
                                  {contact.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{contact.name}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {contact.company_name || 'No company'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              contact.contact_type === 'company'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            }`}>
                              {contact.contact_type === 'company' ? 'Company' : 'Individual'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(contact.updated_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-end">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                {contact.artists_count || 0} artists
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleContactSelect(contact)}
                                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                title="Edit contact"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => handleArchiveClick(contact, e)}
                                className="p-1.5 rounded hover:bg-orange-100 dark:hover:bg-orange-900/20 text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-200"
                                title="Archive contact"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                              {(effectiveHasRole('admin') || effectiveHasRole('super')) && (
                                <button
                                  onClick={(e) => handleDeleteClick(contact, e)}
                                  className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                  title="Delete contact"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </>
          )}

          {activeTab === 'archived' && (
            <>
              {archivedLoading ? (
                <div className="px-6 py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading archived contacts...</p>
                </div>
              ) : archivedContacts.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Archive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No archived contacts</h3>
                  <p className="text-gray-600 dark:text-gray-400">Archived contacts will appear here.</p>
                </div>
              ) : (
                <>
                  <div className="md:hidden space-y-3 p-4">
                    {archivedContacts.map((contact) => renderMobileContactCard(contact, { archived: true }))}
                  </div>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Last Activity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Activity
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {archivedContacts.map((contact) => (
                        <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => handleContactSelect(contact)}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center font-semibold text-white ${
                                  contact.contact_type === 'company' 
                                    ? 'bg-purple-600 dark:bg-purple-600' 
                                    : 'bg-blue-600 dark:bg-blue-600'
                                }`}>
                                  {contact.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{contact.name}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {contact.company_name || 'No company'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              contact.contact_type === 'company'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            }`}>
                              {contact.contact_type === 'company' ? 'Company' : 'Individual'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(contact.updated_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-end">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                {contact.artists_count || 0} artists
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleContactSelect(contact)}
                                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                title="Edit contact"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => handleRestoreClick(contact, e)}
                                className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/20 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200"
                                title="Restore contact"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={(e) => handleDeleteClick(contact, e)}
                                  className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                  title="Delete permanently"
                                >
                                  <Trash2 className="h-4 w-4" />
              </button>
                              )}
            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Contact Modal */}
        <ContactModal
          isOpen={showModal}
          onClose={closeModal}
          contact={selectedContact}
          mode={modalMode}
          onUpdate={async (id, updates) => {
            try {
              const updated = await updateContact(id, updates);
              refetch();
              toast.success(`Contact "${updated?.name}" updated successfully!`);
              return updated;
            } catch (error) {
              console.error('Failed to update contact:', error);
              toast.error('Failed to update contact');
              throw error;
            }
          }}
          onCreate={async (data) => {
            try {
              const created = await createContact(data);
              refetch();
              return created;
            } catch (error) {
              console.error('Failed to create contact:', error);
              throw error;
            }
          }}
          onCreated={() => {
            closeModal();
            toast.success('Contact created successfully!');
          }}
          onDelete={async (id) => {
            try {
              await deleteContact(id);
              refetch();
            } catch (error) {
              console.error('Failed to delete contact:', error);
              throw error;
            }
          }}
        />

        {/* Modals */}
        <ArchiveModal
          isOpen={showArchiveModal}
          onClose={() => {
            setShowArchiveModal(false);
            setSelectedContact(null);
          }}
          onArchive={handleArchiveContact}
          entityName={selectedContact?.name || ''}
          entityType="contact"
        />

        <DeleteModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedContact(null);
          }}
          onDelete={handleDeleteContactPermanent}
          entityName={selectedContact?.name || ''}
          entityType="contact"
          isAdmin={effectiveHasRole('admin') || effectiveHasRole('super')}
        />

        <RestoreModal
          isOpen={showRestoreModal}
          onClose={() => {
            setShowRestoreModal(false);
            setSelectedContact(null);
          }}
          onRestore={handleRestoreContact}
          entityName={selectedContact?.name || ''}
          entityType="contact"
        />

         {/* Create Contact Modal */}
        {showCreateModal && (
           <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
             <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateModal(false)} />
             <div className="absolute inset-x-0 top-10 mx-auto w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
               <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                 <div className="flex items-center gap-2">
                   <Plus className="h-5 w-5 text-purple-500" />
                   <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                     Add New Contact
                   </h3>
                 </div>
                 <button 
                   onClick={() => setShowCreateModal(false)} 
                   className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                 >
                   <X className="h-5 w-5" />
                 </button>
               </div>
               <div className="p-4">
                 <form onSubmit={async (e) => {
                   e.preventDefault();
                   const formData = new FormData(e.currentTarget);
                   const contactData = {
                     name: formData.get('name') as string,
                     contact_type: formData.get('contact_type') as 'individual' | 'company',
                     email: formData.get('email') as string,
                     phone: formData.get('phone') as string,
                     company_name: formData.get('company_name') as string,
                     website: formData.get('website') as string
                   };
                   
                   try {
                     const created = await createContact(contactData);
                     if (created) {
                       setShowCreateModal(false);
                       refetch();
                       toast.success(`Contact "${created.name}" created successfully!`);
                     }
                   } catch (error) {
                     console.error('Failed to create contact:', error);
                     toast.error('Failed to create contact');
                   }
                 }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Contact Type
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                           name="contact_type"
                           value="individual"
                           defaultChecked
                           className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Individual</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                           name="contact_type"
                           value="company"
                           className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600"
                         />
                         <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Company</span>
                      </label>
                    </div>
                  </div>

                    <div>
                     <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                       name="name"
                       required
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter contact name"
                      />
                    </div>

                    <div>
                     <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email
                          </label>
                        <input
                          type="email"
                          id="email"
                       name="email"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="client@example.com"
                        />
                      </div>

                      <div>
                     <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Phone
                          </label>
                        <input
                          type="tel"
                          id="phone"
                       name="phone"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>

                  <div>
                     <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Website
                      </label>
                    <input
                       type="url"
                      id="website"
                       name="website"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://example.com"
                    />
                  </div>

                   <div className="flex items-center justify-end gap-2 pt-4">
                    <button
                      type="button"
                       onClick={() => setShowCreateModal(false)}
                       className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                       className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md"
                     >
                       Create Contact
                              </button>
                            </div>
                </form>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};